import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Download, FileText, Lock, Unlock, ArrowLeft, Search, ShoppingCart, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import BottomNavigation from '../components/BottomNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentResources() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [resources, setResources] = useState([]);
  const [purchasedResourceIds, setPurchasedResourceIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resourcesRes, purchasesRes] = await Promise.all([
        axios.get(`${API}/resources`, getAuthHeader()),
        axios.get(`${API}/resources/my-purchases`, getAuthHeader())
      ]);
      setResources(resourcesRes.data);
      setPurchasedResourceIds(purchasesRes.data.map(p => p.resource_id));
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const isPurchased = (resourceId) => {
    return purchasedResourceIds.includes(resourceId);
  };

  const handleBuy = async (resource) => {
    try {
      toast.info('Redirecting to payment...', { duration: 2000 });
      
      const response = await axios.post(
        `${API}/payments/resource/checkout`,
        { 
          resource_id: resource.id,
          origin_url: window.location.origin
        },
        getAuthHeader()
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleDownload = async (resource) => {
    // Check if resource is paid and not purchased
    if (resource.access_type === 'paid' && resource.price > 0 && !isPurchased(resource.id)) {
      toast.error('Please purchase this resource first');
      return;
    }

    if (resource.file_url) {
      window.open(resource.file_url, '_blank');
      toast.success(`Downloading ${resource.title}...`);
    } else {
      toast.info('Download link not available yet');
    }
  };

  const filteredResources = resources.filter((resource) => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'free' && resource.access_type === 'free') ||
      (activeTab === 'purchased' && (resource.access_type === 'free' || isPurchased(resource.id))) ||
      (activeTab === 'pyp' && resource.type === 'pyp') ||
      (activeTab === 'notes' && resource.type === 'notes');
    return matchesSearch && matchesTab;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white sticky top-0 z-40">
        <div className="px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/student/dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center space-x-2">
              <BookOpen className="w-6 h-6" />
              <span className="text-lg font-bold">EduFlow</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-2">Learning Resources</h1>
          <p className="text-white/80 text-sm">PDFs, Notes, Previous Year Papers</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            data-testid="resource-search-input"
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 rounded-xl border-slate-200 bg-white"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-white rounded-xl p-3 text-center shadow-sm border border-slate-100">
            <p className="text-2xl font-bold text-indigo-600">{resources.length}</p>
            <p className="text-xs text-slate-500">Total</p>
          </Card>
          <Card className="bg-white rounded-xl p-3 text-center shadow-sm border border-slate-100">
            <p className="text-2xl font-bold text-teal-600">{resources.filter(r => r.access_type === 'free').length}</p>
            <p className="text-xs text-slate-500">Free</p>
          </Card>
          <Card className="bg-white rounded-xl p-3 text-center shadow-sm border border-slate-100">
            <p className="text-2xl font-bold text-amber-600">{purchasedResourceIds.length}</p>
            <p className="text-xs text-slate-500">Purchased</p>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="w-full bg-white border border-slate-200 p-1 rounded-xl overflow-x-auto flex-nowrap">
            <TabsTrigger 
              value="all" 
              className="flex-1 min-w-fit rounded-lg data-[state=active]:bg-teal-600 data-[state=active]:text-white text-xs"
            >
              All
            </TabsTrigger>
            <TabsTrigger 
              value="free" 
              className="flex-1 min-w-fit rounded-lg data-[state=active]:bg-teal-600 data-[state=active]:text-white text-xs"
            >
              Free
            </TabsTrigger>
            <TabsTrigger 
              value="purchased" 
              className="flex-1 min-w-fit rounded-lg data-[state=active]:bg-teal-600 data-[state=active]:text-white text-xs"
            >
              My Library
            </TabsTrigger>
            <TabsTrigger 
              value="notes" 
              className="flex-1 min-w-fit rounded-lg data-[state=active]:bg-teal-600 data-[state=active]:text-white text-xs"
            >
              Notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {filteredResources.length === 0 ? (
              <Card className="bg-white rounded-xl p-8 text-center shadow-sm">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No resources found</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredResources.map((resource) => {
                  const isFree = resource.access_type === 'free' || resource.price <= 0;
                  const hasAccess = isFree || isPurchased(resource.id);
                  
                  return (
                    <Card
                      key={resource.id}
                      data-testid={`resource-card-${resource.id}`}
                      className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start space-x-4">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-7 h-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-1">
                              <h3 className="font-bold text-slate-900 truncate pr-2">{resource.title}</h3>
                              {isFree ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-700 flex-shrink-0">
                                  Free
                                </span>
                              ) : hasAccess ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center flex-shrink-0">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Owned
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 flex-shrink-0">
                                  ₹{resource.price}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 line-clamp-2 mb-3">{resource.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-500 uppercase font-medium bg-slate-100 px-2 py-1 rounded">
                                {resource.type}
                              </span>
                              
                              {hasAccess ? (
                                <Button
                                  data-testid={`download-btn-${resource.id}`}
                                  onClick={() => handleDownload(resource)}
                                  size="sm"
                                  className="rounded-full bg-teal-600 hover:bg-teal-700"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  Download
                                </Button>
                              ) : (
                                <Button
                                  data-testid={`buy-btn-${resource.id}`}
                                  onClick={() => handleBuy(resource)}
                                  size="sm"
                                  className="rounded-full bg-amber-500 hover:bg-amber-600"
                                >
                                  <ShoppingCart className="w-4 h-4 mr-2" />
                                  Buy ₹{resource.price}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <BottomNavigation />
    </div>
  );
}
